<template>
  <div class="select-chart-container" :id="id">
    <v-row>
      <v-col cols="3" class="mb-2 mr-3">
        <v-select
          :items="availableItems"
          density="compact"
          variant="outlined"
          v-model="selectedItem"
          :label="selectPrompt"
        />
      </v-col>
      <v-spacer/>
      <v-col cols="auto" class="mb-2 mr-3 m-2">
        <DownloadChartButton :id="id" />
      </v-col>
    </v-row>

    <v-row>
      <v-col cols="12" v-if="selectedItem && chartData.datasets.length < 2">
        <AverageScore
            colorTable="gradientColors"
            :averageScoreOption="true"
            :extra-legend-items="hasAnnotations ? annotations[selectedItem.toLowerCase()].map(a => a.label) : undefined"
            legendItemTitle="Average Score"
            :gradientColors="barChartGradientColorCoding[selectedItem]"
        ></AverageScore>
      </v-col>
      <v-col cols="12">
        <h5 v-if="selectedItem" style="text-align: center; font-size: 22px">
          Individual KPI scores - {{ StringManipulation.methods.clearAndCapitalize(selectedItem) }} level
        </h5>
        <h5 v-else>Please select an item from the list</h5>
      </v-col>
      <v-col cols="12">
        <ColorCodePalette
            v-if="selectedItem != null && comparison"
            :project-color-coding="palette"
            :projects="projects"
        />
        <HorizontalBarChart
            v-if="chartData"
            :chartData="chartData"
            :annotations="hasAnnotations ? annotations[selectedItem.toLowerCase()] : undefined"
            :options="chart.options"
            :v-model="chartData"
            :key="selectedItem"
            :aspectRatio="{ width: 3, height: 1 }"
            class="select-chart-plot"
        >
        </HorizontalBarChart>
      </v-col>
    </v-row>
  </div>
</template>

<script setup>
import StringManipulation from "@/Mixins/StringManipulation";
import HorizontalBarChart from "../base/HorizontalBarChart.vue";
import AverageScore from "../components/AverageScore.vue";
import { computed, onMounted, ref } from "vue";
import Utils from "@/Mixins/Utils";
import DownloadChartButton from "@/Charts/components/DownloadChartButton.vue";
import ColorCodePalette from "@/Shared/ColorCodePalette.vue";

const props = defineProps({
  selectPrompt: {
    type: String,
    required: false,
    default: "Please select an item from the list",
  },
  availableItems: {
    type: Array,
    required: true,
  },
  itemsData: {
    type: Object,
    required: true,
  },
  annotations: {
    type: Object,
    required: true,
    default: () => {
      return {}
    }
  },
  gradientColors: {
    type: Array,
    required: false,
  },
  axisLabels: {
    type: Object,
    required: false,
    default: function () {
      return { x: "", y: "" };
    },
  },
  comparison: {
    type: Boolean,
    default: false,
    required: false
  },
  id: {
    type: String,
    required: false,
  },
});

const selectedItem = ref(null);

const barChartGradientColorCoding = {
  "Building": ["#FADAD2", "#F5B7A6", "#F1937A", "#B43412", "#78230C"],

  "PEB": ["#C7E2FA", "#90C5F6", "#59A9F2", "#0B5294",
    "#073763"],

  "PED": ["#DAEFD3", "#B7DFA8", "#93D07C", "#3E762A",
    "#2A4F1C"],

  "City": ["#FCECD5", "#F9D9AB", "#F6C681", "#C77C0E",
    "#855309"],
}

const projects = computed(() => {
  return props.itemsData[selectedItem.value].data.datasets.map((dataset) => dataset.label);
});

const palette = computed(() => {
  return props.itemsData[selectedItem.value].data.datasets.map((dataset) => dataset.backgroundColor);
});

const chartData = computed(() => {
  if (!selectedItem.value) {
    return null
  }

  const selectedData = props.itemsData[selectedItem.value].data
  const data = Utils.methods.deepCopy(selectedData)

  data.datasets.forEach((d) => {
    d.borderWidth = 1
  })

  renameLabels(data.labels)

  return data
})

const hasAnnotations = computed(() => {
  return Object.keys(props.annotations).length > 0
})

const chart = computed(() => {
  return {
    options: {
      indexAxis: "y",
      plugins: {
        legend: {
          display: false,
        },
      },
      animation: {
        duration: 2000,
      },
      scales: {
        x: {
          stacked: false,
          type: "linear",
          id: "x-axis-1",
          position: "top",
          max: 5,
          ticks: {
            color: '1e1e1e',
            font: {
              size: 15
            }
          },
        },
        'bar-x-axis1': {
          max: 5,
          axis: 'x',
          position: 'bottom',
          grid: {
            drawOnChartArea: false
          },
          ticks: {
            color: '1e1e1e',
            font: {
              size: 15
            }
          }
        },
        y: {
          ticks: {
            color: '1e1e1e',
            autoSkip: false,
          }
        }
      },
      barPercentage: 0.8,
      categoryPercentage: 0.9,
    },
  };
});

onMounted(() => {
  selectedItem.value = props.availableItems[0];
});

/**
 * Renames the labels in the provided array by clearing and capitalizing each label.
 *
 * @param {string[]} labelsArray - An array of label strings to be modified.
 * @return {void} This method does not return a value; it directly modifies the input array.
 */
function renameLabels(labelsArray) {
  labelsArray.forEach((label, index) => {
    labelsArray[index] = StringManipulation.methods.clearAndCapitalize(label);
  });
}
</script>

<style scoped></style>
